import React from 'react';
import * as yup from 'yup';
import { Select } from '@a-ui/react';
import { ObjectShape } from 'yup/lib/object';
import CheckGroup from '../../components/CheckGroup';
import RadioGroup from '../../components/RadioGroup';
import Switch from '../../components/Switch';
import { useAuthentication } from '../../state/Authentication';

export const useDomainSchema = (schema: Api.Domain) => {
  const {
    state: { user },
  } = useAuthentication();

  const data = React.useMemo(() => {
    if (!schema) return undefined;

    return schema.preferences.reduce((result, preference) => {
      const value = user.domains?.[schema.key]?.preferences?.[preference.key];
      if (!value) return result;

      return {
        ...result,
        [preference.key]: value,
      };
    }, {} as Api.UserDomain['preferences']);
  }, [schema, user]);

  const humanReadableData = React.useMemo(() => {
    if (!schema) return undefined;

    return schema.preferences.reduce((result, preference) => {
      let value = user.domains?.[schema.key].preferences?.[preference.key];

      if (preference.type === 'switch') {
        value = preference.values.find((v) => v.value === value)?.label || '';
      } else {
        const array = [value].flat();

        value = array
          .map((v) => {
            const pref = preference.values.find((item) => item.value === v);
            return pref?.label || v;
          })
          .join(', ');
      }

      return {
        ...result,
        [preference.label]: value,
      };
    }, {} as Record<string, string | number>);
  }, [schema, user]);

  const renderForm = React.useCallback(() => {
    if (!schema) return undefined;

    return (
      <>
        {schema.preferences.map((preference) => {
          const props = {
            key: `${schema.key}-${preference.key}`,
            label: preference.label,
            name: preference.key,
            classNames: 'u-margin-bottom',
            description: preference.description,
            required: false,
          };

          const options = preference.values.map(({ value, label }) => ({
            label,
            value,
          }));

          if (preference.type === 'multiple') {
            return <CheckGroup options={options} {...props} />;
          }

          if (preference.type === 'radio') {
            return <RadioGroup options={options} {...props} />;
          }

          if (preference.type === 'switch') {
            return <Switch off={preference.values[0]} on={preference.values[1]} {...props} />;
          }

          return <Select options={options} {...props} />;
        })}
      </>
    );
  }, [schema]);

  const validationSchema = React.useMemo(() => {
    if (!schema) return undefined;

    const s = schema.preferences.reduce((result, preference) => {
      let validation: yup.AnySchema = yup.string();

      if (preference.type === 'multiple') {
        validation = yup.array().of(yup.mixed().oneOf(preference.values.map((v) => v.value)));
      } else {
        validation = validation.oneOf(preference.values.map((p) => p.value));
      }

      return {
        ...result,
        [preference.key]: validation,
      };
    }, {} as ObjectShape);

    return yup.object().shape(s);
  }, [schema]);

  return {
    data,
    humanReadableData,
    schema,
    renderForm,
    validationSchema,
  };
};
